import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader } from "@/components/ui/card";

// Dashboard stat card skeleton
export const DashboardStatSkeleton = () => (
  <Card>
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <Skeleton className="h-4 w-24" />
      <Skeleton className="h-4 w-4" />
    </CardHeader>
    <CardContent>
      <Skeleton className="h-8 w-16 mb-2" />
      <Skeleton className="h-3 w-32" />
    </CardContent>
  </Card>
);

// Dashboard stats grid skeleton
export const DashboardStatsGridSkeleton = () => (
  <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
    {Array.from({ length: 4 }).map((_, i) => (
      <DashboardStatSkeleton key={i} />
    ))}
  </div>
);

// Table row skeleton
export const TableRowSkeleton = ({ columns = 4 }: { columns?: number }) => (
  <tr>
    {Array.from({ length: columns }).map((_, i) => (
      <td key={i} className="px-6 py-4">
        <Skeleton className="h-4 w-full" />
      </td>
    ))}
  </tr>
);

// Table skeleton
export const TableSkeleton = ({ 
  rows = 5, 
  columns = 4, 
  headers 
}: { 
  rows?: number; 
  columns?: number; 
  headers?: string[];
}) => (
  <div className="relative overflow-auto">
    <table className="w-full caption-bottom text-sm">
      <thead className="[&_tr]:border-b">
        <tr className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
          {headers ? 
            headers.map((header, i) => (
              <th key={i} className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
                {header}
              </th>
            )) :
            Array.from({ length: columns }).map((_, i) => (
              <th key={i} className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
                <Skeleton className="h-4 w-20" />
              </th>
            ))
          }
        </tr>
      </thead>
      <tbody className="[&_tr:last-child]:border-0">
        {Array.from({ length: rows }).map((_, i) => (
          <TableRowSkeleton key={i} columns={columns} />
        ))}
      </tbody>
    </table>
  </div>
);

// Chart area skeleton
export const ChartSkeleton = ({ height = "h-96" }: { height?: string }) => (
  <Card>
    <CardHeader>
      <Skeleton className="h-6 w-48" />
      <Skeleton className="h-4 w-32" />
    </CardHeader>
    <CardContent>
      <div className={`${height} flex items-end justify-between px-4 pb-4`}>
        {Array.from({ length: 12 }).map((_, i) => (
          <Skeleton 
            key={i} 
            className="w-8" 
            style={{ height: `${Math.random() * 60 + 20}%` }}
          />
        ))}
      </div>
    </CardContent>
  </Card>
);

// Settings user card skeleton
export const UserCardSkeleton = () => (
  <tr>
    <td className="px-6 py-4">
      <div className="flex items-center space-x-3">
        <Skeleton className="h-8 w-8 rounded-full" />
        <Skeleton className="h-4 w-32" />
      </div>
    </td>
    <td className="px-6 py-4">
      <Skeleton className="h-4 w-48" />
    </td>
    <td className="px-6 py-4">
      <div className="flex items-center gap-2">
        <Skeleton className="h-6 w-16 rounded-full" />
        <Skeleton className="h-6 w-24 rounded-full" />
      </div>
    </td>
    <td className="px-6 py-4">
      <div className="flex items-center gap-2">
        <Skeleton className="h-8 w-24" />
        <Skeleton className="h-8 w-20" />
      </div>
    </td>
  </tr>
);

// Settings users table skeleton
export const UsersTableSkeleton = () => (
  <div className="relative overflow-auto">
    <table className="w-full caption-bottom text-sm">
      <thead className="[&_tr]:border-b">
        <tr className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
          <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Name</th>
          <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Email</th>
          <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Role</th>
          <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Actions</th>
        </tr>
      </thead>
      <tbody className="[&_tr:last-child]:border-0">
        {Array.from({ length: 5 }).map((_, i) => (
          <UserCardSkeleton key={i} />
        ))}
      </tbody>
    </table>
  </div>
);

// Generic content skeleton with title
export const ContentSkeleton = ({ 
  title = true, 
  description = true, 
  lines = 3 
}: { 
  title?: boolean; 
  description?: boolean; 
  lines?: number;
}) => (
  <div className="space-y-4">
    {title && <Skeleton className="h-8 w-64" />}
    {description && <Skeleton className="h-4 w-96" />}
    <div className="space-y-2">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} className="h-4 w-full" />
      ))}
    </div>
  </div>
);

// Production dashboard skeleton
export const ProductionDashboardSkeleton = () => (
  <div className="space-y-6">
    <div className="grid gap-4 md:grid-cols-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <Card key={i}>
          <CardHeader>
            <Skeleton className="h-5 w-32" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-8 w-16" />
          </CardContent>
        </Card>
      ))}
    </div>
    <ChartSkeleton height="h-64" />
  </div>
);